import type { GetProjectResponse } from '../api/types';
import type { Project } from '../Components/Projects/projects';
import { migrateProject } from './projectMigrations';
import { applyProjectVersionDefaults, cloneProjectVersion, compareProjectVersions, pickProjectVersionFields } from './projectVersions';

type ProjectStringResponse = Pick<GetProjectResponse, 'project_string'> & Partial<Pick<GetProjectResponse, 'version' | 'framework_version'>>;

/**
 * Merge API envelope version fields onto a project.
 * Call after {@link migrateProject} on the serialized body so envelope CURRENT cannot skip upgrades.
 */
export function applyProjectApiMetadata<T extends Project>(project: T, source: unknown): T {
  const versions = pickProjectVersionFields(source);
  return {
    ...project,
    ...(versions.version && { version: cloneProjectVersion(versions.version) }),
    ...(versions.frameworkVersion && { frameworkVersion: cloneProjectVersion(versions.frameworkVersion) }),
  } as T;
}

/**
 * Apply envelope metadata after migration without downgrading a version stamp migration just wrote
 * (e.g. stale bundled/cache metadata still advertising 1.0.0).
 */
export function mergeApiMetadataAfterMigration<T extends Project>(migrated: T, envelope: unknown): T {
  const withEnvelope = applyProjectApiMetadata(migrated, envelope);
  const migratedVersion = pickProjectVersionFields(migrated).version;
  const mergedVersion = pickProjectVersionFields(withEnvelope).version;

  if (migratedVersion && mergedVersion && compareProjectVersions(migratedVersion, mergedVersion) > 0) {
    return {
      ...withEnvelope,
      version: cloneProjectVersion(migratedVersion),
    };
  }

  return withEnvelope;
}

export function parseProjectFromApiResponse(response: ProjectStringResponse): Project {
  // Migrate from the project_string body first (missing version ⇒ legacy). Envelope metadata may
  // already be CURRENT from create/request defaults and must not gate migrations.
  const migrated = migrateProject(JSON.parse(response.project_string) as Project);
  return applyProjectVersionDefaults(mergeApiMetadataAfterMigration(migrated, response));
}
